import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Envelope, Plus, PlusCircle, Plugs, DotsThree, Trash, Star,
  Globe, CircleNotch, ShieldCheck, PencilSimple, UsersThree, CaretRight,
} from '@phosphor-icons/react'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Select, type SelectGroup } from '@/components/ui/Select'
import { useSenderEmailStore } from '@/stores/senderEmailStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { usePermission } from '@/hooks/usePermission'
import { usePlanFeatures } from '@/hooks/usePlanFeatures'
import { workflowsApi, type AssignableMember } from '@/lib/api'
import DnsStatusBadge from './DnsStatusBadge'
import AddSenderEmailModal from '@/pages/sender-emails/AddSenderEmailModal'
import EditSenderEmailModal from '@/pages/sender-emails/EditSenderEmailModal'
import WarmupSection from '@/components/warmup/WarmupSection'
import { isFullyVerified } from '@/lib/senderVerification'
import EmailSignatureSection from './EmailSignatureSection'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import ManageAccessModal from './ManageAccessModal'
import { ReadOnlyBanner, SectionLocked } from './SectionAccess'
import { cardChromeClass, inputStyle } from '@/lib/styleKit'
import { nextTabIndex } from '@/lib/rovingTabs'
import type { SenderEmail } from '@/types/senderEmail'
import { useUnsavedChangesGuard } from '@/lib/useUnsavedChangesGuard'
import { withDirty } from '@/lib/dirtySet'
import mailboxesStackIcon from '@/assets/mailboxes-stack-icon.png'

// Display names for team roles in the Access dropdown (mirrors
// TeamSection's ROLE_LABELS — the internal role values never change).
const MEMBER_ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  admin: 'Admin',
  manager: 'Manager',
  representative: 'Representative',
  rep: 'Representative', // legacy value predating the rename
  analyst: 'Analyst',
}

// Normalizes the legacy 'rep' role value into 'representative' so it groups
// with the current one instead of forming its own singleton "Rep" bucket.
const normalizeRole = (role: string): string => (role === 'rep' ? 'representative' : role)

// Assignable-by-role grouping order (below Admin — see ASSIGNABLE below for
// why Owner/Admin never appear here at all).
const ASSIGNABLE_ROLE_ORDER = ['manager', 'representative', 'analyst']

const STATUS_PILL = 'inline-flex items-center px-2 py-0.5 rounded-full border uppercase tracking-[0.06em]'
const statusConfig: Record<string, { label: string; className: string }> = {
  pending:   { label: 'Pending',   className: 'bg-amber-500/10 border-amber-500/25 text-amber-700 dark:bg-amber-500/[0.12] dark:border-amber-500/30 dark:text-amber-300' },
  verifying: { label: 'Verifying', className: 'bg-[#5e6ad2]/[0.10] border-[#5e6ad2]/25 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.14] dark:border-[#5e6ad2]/30 dark:text-[#a5b4fc]' },
  verified:  { label: 'Verified',  className: 'bg-emerald-500/10 border-emerald-500/25 text-emerald-700 dark:bg-emerald-500/[0.12] dark:border-emerald-500/30 dark:text-emerald-300' },
  failed:    { label: 'Failed',    className: 'bg-red-500/10 border-red-500/25 text-red-700 dark:bg-red-500/[0.12] dark:border-red-500/30 dark:text-red-300' },
}

// Scalloped verified seal (Material-style). Body uses currentColor; the check
// punch-out is white so it reads as a positive verification mark on the brand
// indigo seal in both modes.
function VerifiedSeal({ size = 16, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={className}
    >
      <path
        d="M23 12l-2.44-2.78.34-3.68-3.61-.82-1.89-3.18L12 3 8.6 1.54 6.71 4.72l-3.61.81.34 3.68L1 12l2.44 2.78-.34 3.69 3.61.82 1.89 3.18L12 21l3.4 1.46 1.89-3.18 3.61-.82-.34-3.68L23 12zM10 17l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"
        fill="currentColor"
      />
    </svg>
  )
}

const EmailAccountsSection: React.FC = () => {
  const {
    senderEmails, isLoading,
    fetchSenderEmails, deleteSenderEmail,
    verifySenderEmail, checkDns, setDefault, assignSender,
  } = useSenderEmailStore()
  // Owner/Admin grant mailbox access: a team member sees/uses ONLY the
  // mailboxes assigned to them (unassigned = admins only, not a shared
  // pool) — the backend enforces this on every list and at campaign
  // create/launch; this dropdown is just the assignment control.
  const { canWrite: canAssign } = usePermission('sender_emails', 'assign')
  // Manager's "view" level on sender_emails.manage sees this section but
  // can't add/edit/delete/verify/check-DNS/set-default -- backend already
  // 403s these (routes/sender_emails.py checks `perm.scope == "view"` on
  // every mutation), this just keeps the UI from offering a control that
  // would only 403. Owner/Admin keep full write access ("manage").
  const { canWrite, scope: senderEmailsScope, allowed: senderEmailsAllowed } = usePermission('sender_emails', 'manage')
  // Adding a mailbox is Owner/Admin only (AB#22516). Manager/Representative hold
  // "own" and keep managing the mailboxes assigned to them, but are not offered
  // any Add button. UI only, the backend rule is unchanged.
  const canAddMailbox = canWrite && senderEmailsScope !== 'own'
  // On Momentum/Enterprise/Trial, the signature is the logged-in user's own
  // (Settings > Signatures), the same value regardless of which mailbox row
  // this is -- so every row points at it instead of rendering its own editor.
  const { has } = usePlanFeatures()
  const hasPerUserSignatures = has('per_user_signatures')
  const setActiveSection = useSettingsStore((s) => s.setActiveSection)
  const [members, setMembers] = useState<AssignableMember[]>([])
  // Owner/Admin already bypass mailbox assignment entirely — every route that
  // checks access (services/sender_access.py: can_manage_sender_assignments)
  // grants them every sender in the account unconditionally, regardless of
  // SenderEmailAssignment rows. Granting one explicitly would be a no-op that
  // only clutters the picker and the assigned-pill row (and the viewer here is
  // always an Owner/Admin themselves — `canAssign` requires "manage" on
  // sender_emails.assign, which only they hold — so this also covers "can't
  // add myself"). Never offered as an option, individually or by role.
  const assignableMembers = useMemo(
    () => members.filter(m => m.role !== 'owner' && m.role !== 'admin'),
    [members],
  )
  // "Add by role" group: one entry per role actually present among
  // assignableMembers, in ASSIGNABLE_ROLE_ORDER. Always offered (not hidden
  // once everyone of that role is already granted) — it reads as "grant every
  // Rep access," a standing action, not a one-shot picker entry; re-selecting
  // it once everyone's already in is a harmless no-op via the Set union above.
  const roleGroupOptions = useMemo(
    () => ASSIGNABLE_ROLE_ORDER
      .map(role => ({
        role,
        count: assignableMembers.filter(m => normalizeRole(m.role) === role).length,
      }))
      .filter(({ count }) => count > 0)
      .map(({ role, count }) => ({
        value: `role:${role}`,
        label: `All ${MEMBER_ROLE_LABELS[role]}s`,
        description: `${count} ${count === 1 ? 'person' : 'people'}`,
      })),
    [assignableMembers],
  )
  // Builds the Access picker's groups for one sender: role-bulk options first
  // (the broadest, most common grant), then individual teammates not already
  // granted. A group with zero options is dropped so an account with e.g. no
  // Analysts doesn't show an empty "Add by role" header.
  const accessGroupsFor = useCallback((assignedUserIds: number[]): SelectGroup[] => {
    const individualOptions = assignableMembers
      .filter(m => !assignedUserIds.includes(m.id))
      .map(m => ({
        value: String(m.id),
        // Name as the primary line, muted "email · Role" beneath (Select's
        // built-in description line) — no more single crammed string.
        label: m.full_name || m.email,
        description: m.full_name
          ? `${m.email} · ${MEMBER_ROLE_LABELS[m.role] ?? m.role}`
          : MEMBER_ROLE_LABELS[m.role] ?? m.role,
      }))
    return [
      { label: 'Add by role', options: roleGroupOptions },
      { label: 'Add individually', options: individualOptions },
    ].filter(g => g.options.length > 0)
  }, [assignableMembers, roleGroupOptions])
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingSender, setEditingSender] = useState<SenderEmail | null>(null)
  // Id, not the row object — so the modal re-derives from the live
  // `senderEmails` array on every render and reflects a revoke immediately
  // (assignSender refetches the list) instead of showing a stale snapshot
  // until the modal is closed and reopened.
  const [accessModalSenderId, setAccessModalSenderId] = useState<number | null>(null)
  const [openMenu, setOpenMenu] = useState<number | null>(null)
  const [menuCoords, setMenuCoords] = useState<{ top: number; right: number } | null>(null)
  const [verifyingId, setVerifyingId] = useState<number | null>(null)
  const [pendingDelete, setPendingDelete] = useState<SenderEmail | null>(null)
  const [modalDirty, setModalDirty] = useState(false)
  // Keyed by sender id -- EmailSignatureSection renders once per row, always
  // mounted (not an accordion), so more than one can be dirty at once.
  const [dirtySignatureIds, setDirtySignatureIds] = useState<Set<number>>(new Set())
  // withDirty returns `prev` itself when the membership is unchanged. Each
  // editor reports dirty=false on mount and on cleanup; an updater that
  // always returned a fresh Set re-rendered this section on every one of
  // those reports, handed the editor a new inline callback, and re-ran its
  // effect: "Maximum update depth exceeded" on this page.
  const handleSignatureDirtyChange = useCallback((senderId: number, dirty: boolean) => {
    setDirtySignatureIds((prev) => withDirty(prev, senderId, dirty))
  }, [])

  useUnsavedChangesGuard(modalDirty || dirtySignatureIds.size > 0)

  const triggerRefs = useRef<Record<number, HTMLButtonElement | null>>({})
  const menuRef = useRef<HTMLDivElement | null>(null)

  // Analyst holds no sender_emails.manage cell (spec sect.4-D), so
  // GET /sender-emails 403s for them -- don't fire it (AB#22341). The nav
  // hides this section for them; this covers a stored activeSection.
  useEffect(() => {
    if (!senderEmailsAllowed) return
    fetchSenderEmails()
  }, [senderEmailsAllowed])

  // Teammate options for the Access dropdown — only fetched when the viewer
  // can actually assign (Owner/Admin); everyone else never renders it.
  useEffect(() => {
    if (!canAssign) return
    workflowsApi.assignableMembers()
      .then(res => setMembers(res.data.members))
      .catch(() => {})
  }, [canAssign])

  // Memoised so the dismissal effect below can list it as a dependency instead
  // of closing over a stale copy: it reads `openMenu`, which is exactly what
  // that effect re-subscribes on.
  const closeMenu = useCallback((restoreFocus = true) => {
    const id = openMenu
    setOpenMenu(null)
    setMenuCoords(null)
    // Focus goes back to the trigger the menu came from — except when the user
    // dismissed by clicking elsewhere, where yanking focus back would fight
    // whatever they just clicked.
    if (restoreFocus && id != null) triggerRefs.current[id]?.focus()
  }, [openMenu])

  const toggleMenu = (id: number, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (openMenu === id) { closeMenu(); return }
    const rect = e.currentTarget.getBoundingClientRect()
    setMenuCoords({ top: rect.bottom + 6, right: window.innerWidth - rect.right })
    setOpenMenu(id)   // one id at a time — opening one closes the other
  }

  // §19 dismissal contract: outside pointerdown, Escape, scroll (capture, so
  // nested scroll containers count) and resize. The menu is portalled and
  // fixed-positioned, so stale coords must dismiss rather than drift.
  useEffect(() => {
    if (openMenu == null) return
    const id = openMenu
    const onDismiss = () => closeMenu(false)
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null
      if (!target) return
      if (menuRef.current?.contains(target)) return
      // A pointerdown on the trigger itself is the toggle — let its onClick run.
      if (triggerRefs.current[id]?.contains(target)) return
      closeMenu(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); closeMenu() }
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    window.addEventListener('scroll', onDismiss, true)
    window.addEventListener('resize', onDismiss)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      window.removeEventListener('scroll', onDismiss, true)
      window.removeEventListener('resize', onDismiss)
      document.removeEventListener('keydown', onKey)
    }
  }, [openMenu, closeMenu])

  // Land focus on the first item when the menu opens, so Arrow keys have a
  // starting point and a keyboard user isn't left focused on the trigger.
  useEffect(() => {
    if (openMenu == null) return
    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not([disabled])')?.focus()
  }, [openMenu])

  const onMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(
      e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not([disabled])'),
    )
    if (items.length === 0) return
    const current = items.indexOf(document.activeElement as HTMLButtonElement)
    const next = nextTabIndex(e.key, current, items.length, 'vertical')
    if (next === null) return
    e.preventDefault()
    items[next]?.focus()
  }

  const confirmDelete = () => {
    if (!pendingDelete) return
    const id = pendingDelete.id
    setPendingDelete(null)        // close dialog immediately — single clean transition
    void deleteSenderEmail(id)    // optimistic store removal handles the UI
  }

  const handleVerify = async (id: number) => {
    setVerifyingId(id)
    try { await verifySenderEmail(id) } finally { setVerifyingId(null) }
  }

  const stats = {
    total: senderEmails.length,
    verified: senderEmails.filter(s => s.status === 'verified').length,
    pending: senderEmails.filter(s => s.status === 'pending').length,
    failed: senderEmails.filter(s => s.status === 'failed').length,
  }

  // §59: a menu opened from a page sits in the page-menu band — z-[80]
  // backdrop / z-[81] surface — even though it is portalled to document.body.
  // It used to render inline at z-10, the in-flow-chrome band, inside a card
  // that clips it.
  const MENU = 'z-[81] w-44 bg-card dark:bg-[#191a1b] rounded-lg shadow-lg border border-border/60 dark:border-white/[0.08] py-1.5'
  const MENU_ITEM = 'w-full text-left px-3 py-2 text-foreground/80 dark:text-[#d0d6e0] hover:bg-muted/50 dark:hover:bg-white/[0.04] focus:outline-none focus-visible:bg-muted/60 dark:focus-visible:bg-white/[0.06] transition-colors duration-150 flex items-center gap-2 disabled:opacity-60'
  const STAT_TONES = {
    foreground: 'text-foreground dark:text-[#f7f8f8]',
    success: 'text-emerald-700 dark:text-emerald-300',
    warn: 'text-amber-700 dark:text-amber-300',
    error: 'text-red-700 dark:text-red-300',
  }

  // Re-derived from the live `senderEmails` array (not a captured snapshot)
  // so a revoke inside ManageAccessModal — which refetches the list — shows
  // up there immediately instead of only after the modal is closed and
  // reopened.
  const accessModalSender = accessModalSenderId != null
    ? senderEmails.find(s => s.id === accessModalSenderId) ?? null
    : null

  if (!senderEmailsAllowed) {
    return (
      <SectionLocked title="Mailboxes are managed by your account owner">
        Your role doesn&apos;t include access to the workspace&apos;s sender
        mailboxes or their SMTP configuration. Ask a workspace Owner or Admin
        if you need one set up.
      </SectionLocked>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2
            className="text-foreground dark:text-[#f7f8f8]"
            style={{ fontSize: '24px', fontWeight: 590, letterSpacing: '-0.288px', lineHeight: 1.33 }}
          >
            Mailboxes
          </h2>
          <p
            className="text-muted-foreground dark:text-[#8a8f98] mt-1"
            style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
          >
            Connect the inboxes your campaigns send from.
          </p>
        </div>
        {canAddMailbox && (
          <Button variant="default" size="sm" onClick={() => setShowAddModal(true)} icon={<Plus className="w-4 h-4" weight="bold" />}>
            Add mailbox
          </Button>
        )}
      </div>

      {!canWrite && (
        <ReadOnlyBanner>
          You can see the account's mailboxes and their verification/DNS status,
          but only a workspace Owner or Admin can add, edit, delete, verify, or
          reconfigure them.
        </ReadOnlyBanner>
      )}

      {/* Stats. Four zeros above an empty list say nothing, so the tiles wait
          for the first mailbox. */}
      {!isLoading && senderEmails.length > 0 && (
      <div className="grid grid-cols-4 gap-3">
        {([
          { label: 'Total', value: stats.total, tone: STAT_TONES.foreground },
          { label: 'Verified', value: stats.verified, tone: STAT_TONES.success },
          { label: 'Pending', value: stats.pending, tone: STAT_TONES.warn },
          { label: 'Failed', value: stats.failed, tone: STAT_TONES.error },
        ]).map(s => (
          <div key={s.label} className="bg-muted/40 dark:bg-white/[0.02] border border-border/50 dark:border-white/[0.08] rounded-lg p-4 text-center">
            <p className={`tabular-nums ${s.tone}`} style={{ fontSize: '22px', fontWeight: 590, letterSpacing: '-0.24px', lineHeight: 1.1 }}>{s.value}</p>
            <p className="text-muted-foreground dark:text-[#62666d] uppercase tracking-[0.06em] mt-1" style={{ fontSize: '10px', fontWeight: 590 }}>{s.label}</p>
          </div>
        ))}
      </div>
      )}

      {/* Sender list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <CircleNotch className="w-6 h-6 animate-spin text-muted-foreground/60 dark:text-[#62666d]" />
        </div>
      ) : senderEmails.length === 0 ? (
        /* First-run empty state (design-aesthetics sect. 24). The chip is brand
           indigo rather than the neutral muted square because connecting a
           mailbox is THE action on this screen, not a side note. */
        <Card className={cardChromeClass}>
          <CardContent className="p-14 text-center">
            <div className="max-w-md mx-auto">
              <div className="h-14 w-14 rounded-full bg-[#5e6ad2]/[0.10] dark:bg-[#5e6ad2]/[0.16] border border-[#5e6ad2]/20 dark:border-[#5e6ad2]/25 flex items-center justify-center mx-auto mb-5">
                <Envelope className="w-6 h-6 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="regular" />
              </div>
              <h3
                className="text-foreground dark:text-[#f7f8f8] mb-2"
                style={{ fontSize: '20px', fontWeight: 590, lineHeight: 1.33, letterSpacing: '-0.24px' }}
              >
                Connect your mailbox
              </h3>
              <p
                className="text-muted-foreground dark:text-[#8a8f98] leading-relaxed"
                style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
              >
                Beevelope sends your campaigns from your own mailbox.{' '}
                {canAddMailbox
                  ? 'Connect your first one to start sending.'
                  : 'Ask a workspace Owner or Admin to connect the first one.'}
              </p>
              {canAddMailbox && (
                <Button
                  variant="default"
                  size="sm"
                  className="mt-6"
                  onClick={() => setShowAddModal(true)}
                  icon={<Plugs className="w-4 h-4" weight="bold" />}
                >
                  Connect mailbox
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {/* Deep-link anchors (see SettingsNav) sit on the first row only — a
              slug has to resolve to one element, and the first row is what a
              search result should land on. */}
          {senderEmails.map((sender, idx) => {
            const sc = statusConfig[sender.status] || statusConfig.pending
            const anchor = (slug: string) => (idx === 0 ? slug : undefined)
            return (
              <Card key={sender.id} variant="default" className={`${cardChromeClass} transition-colors duration-150`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Holds the verification verdict (seal / status pill). */}
                      <div className="flex items-center gap-2 mb-1 flex-wrap" data-settings-anchor={anchor('email-accounts-verify-connection')}>
                        <p className="text-foreground dark:text-[#f7f8f8] truncate" style={{ fontSize: '14px', fontWeight: 590, letterSpacing: '-0.13px' }}>
                          {sender.label || sender.email_address}
                        </p>
                        {sender.is_default && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border bg-amber-500/10 border-amber-500/25 text-amber-700 dark:bg-amber-500/[0.12] dark:border-amber-500/30 dark:text-amber-300 uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 590 }}>
                            <Star className="w-2.5 h-2.5" weight="fill" /> Default
                          </span>
                        )}
                        {sender.status === 'verified' ? (
                          <VerifiedSeal
                            size={16}
                            className="text-[#5e6ad2] dark:text-[#a5b4fc] flex-shrink-0"
                          />
                        ) : (
                          <span className={`${STATUS_PILL} ${sc.className}`} style={{ fontSize: '10px', fontWeight: 590 }}>
                            {sc.label}
                          </span>
                        )}
                      </div>
                      <p className="text-muted-foreground dark:text-[#8a8f98] font-mono" style={{ fontSize: '12px', letterSpacing: '-0.05px' }} data-settings-anchor={anchor('email-accounts-sender-email')}>{sender.email_address}</p>
                      <div className="flex items-center gap-3 mt-2.5" data-settings-anchor={anchor('email-accounts-dns')}>
                        <DnsStatusBadge spf={sender.spf_valid} dkim={sender.dkim_valid} dmarc={sender.dmarc_valid} />
                        <span className="text-muted-foreground dark:text-[#62666d] tabular-nums uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 510 }} data-settings-anchor={anchor('email-accounts-daily-send-limit')}>
                          {sender.emails_sent_today}/{sender.daily_send_limit} sent today
                        </span>
                      </div>
                      {canAssign && (
                        <div className="mt-2.5" data-settings-anchor={anchor('email-accounts-access')}>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground dark:text-[#62666d] uppercase tracking-[0.06em]" style={{ fontSize: '10px', fontWeight: 590 }}>
                              Access
                            </span>
                            <Select
                              value=""
                              onChange={(v) => {
                                if (!v) return
                                const current = sender.assigned_user_ids ?? []
                                if (v.startsWith('role:')) {
                                  // Bulk-by-role: union in every assignable member of
                                  // that role (idempotent — already-granted ids just
                                  // no-op through the Set).
                                  const role = v.slice('role:'.length)
                                  const roleIds = assignableMembers
                                    .filter(m => normalizeRole(m.role) === role)
                                    .map(m => m.id)
                                  assignSender(sender.id, Array.from(new Set([...current, ...roleIds])))
                                } else {
                                  assignSender(sender.id, [...current, Number(v)])
                                }
                              }}
                              groups={accessGroupsFor(sender.assigned_user_ids ?? [])}
                              placeholder="Grant access…"
                              ariaLabel={`Grant access to ${sender.label || sender.email_address}`}
                              minWidth={220}
                            />
                          </div>
                          <div className="mt-1.5">
                            {(sender.assigned_user_ids ?? []).length === 0 ? (
                              <span className="text-muted-foreground dark:text-[#62666d]" style={{ fontSize: '11px' }}>
                                Owners &amp; admins only
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setAccessModalSenderId(sender.id)}
                                className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors duration-150"
                                style={{ fontSize: '11px', fontWeight: 510 }}
                              >
                                <UsersThree className="h-3.5 w-3.5" weight="regular" />
                                {sender.assigned_user_ids!.length} {sender.assigned_user_ids!.length === 1 ? 'person' : 'people'} with access
                                <CaretRight className="h-3 w-3" weight="bold" />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {canWrite && (
                      <div className="flex-shrink-0">
                        <button
                          type="button"
                          ref={el => { triggerRefs.current[sender.id] = el }}
                          onClick={e => toggleMenu(sender.id, e)}
                          className="p-1.5 hover:bg-muted/50 dark:hover:bg-white/[0.04] rounded-md transition-colors duration-150"
                          aria-haspopup="menu"
                          aria-expanded={openMenu === sender.id}
                          aria-controls={openMenu === sender.id ? `sender-menu-${sender.id}` : undefined}
                          aria-label={`Actions for ${sender.label || sender.email_address}`}
                        >
                          <DotsThree className="w-4 h-4 text-muted-foreground dark:text-[#8a8f98]" weight="bold" />
                        </button>
                      </div>
                    )}
                  </div>
                  {/* Both panels own their own markup, so the deep-link anchor
                      goes on a bare wrapper. It has no border or padding, so the
                      panel's own `mt-4` collapses through it — spacing unchanged. */}
                  <div data-settings-anchor={anchor('email-accounts-warmup')}>
                    <WarmupSection
                      senderEmailId={sender.id}
                      smtpVerified={sender.smtp_verified}
                      connectionType={sender.connection_type}
                    />
                  </div>
                  <div data-settings-anchor={anchor('email-accounts-email-signature')}>
                    {hasPerUserSignatures ? (
                      <p className="text-muted-foreground dark:text-[#8a8f98] mt-2" style={{ fontSize: '12px' }}>
                        <button
                          type="button"
                          role="link"
                          onClick={() => setActiveSection('signatures')}
                          aria-label="Your signature is set in Settings → Signatures, not per mailbox"
                          className="text-[#5e6ad2] dark:text-[#a5b4fc] underline-offset-2 hover:underline"
                        >
                          Your signature is set in Settings → Signatures
                        </button>
                        , not per mailbox.
                      </p>
                    ) : (
                      <EmailSignatureSection
                        senderEmailId={sender.id}
                        initialSignatureHtml={sender.signature_html}
                        onDirtyChange={(dirty) => handleSignatureDirtyChange(sender.id, dirty)}
                        readOnly={!canWrite}
                      />
                    )}
                  </div>
                </CardContent>
              </Card>
            )
          })}

          {/* Add-more band. Sits under the last mailbox so the next step is
              always in view at the end of the list. Owner/Admin only: for a
              read-only role it would be a button that 403s. */}
          {canAddMailbox && (
            <div className="flex items-center gap-5 flex-wrap rounded-lg border border-[#5e6ad2]/15 dark:border-[#5e6ad2]/20 bg-[#5e6ad2]/[0.05] dark:bg-[#5e6ad2]/[0.08] px-5 py-4">
              {/* Decorative illustration, bundled by Vite so the build fails
                  loudly if the file is missing. */}
              <img
                src={mailboxesStackIcon}
                alt=""
                aria-hidden="true"
                className="h-14 w-auto flex-shrink-0 select-none"
                draggable={false}
              />
              <div className="flex-1 min-w-[220px]">
                <p
                  className="text-foreground dark:text-[#f7f8f8]"
                  style={{ fontSize: '15px', fontWeight: 590, letterSpacing: '-0.18px', lineHeight: 1.4 }}
                >
                  Add more mailboxes
                </p>
                <p
                  className="text-muted-foreground dark:text-[#8a8f98] mt-0.5"
                  style={{ fontSize: '13px', letterSpacing: '-0.13px', lineHeight: 1.5 }}
                >
                  Spreading your sending across several mailboxes means each one sends less.
                  That protects your deliverability and gives you room to scale.
                </p>
              </div>
              <Button
                variant="default"
                size="sm"
                className="flex-shrink-0"
                onClick={() => setShowAddModal(true)}
                icon={<PlusCircle className="w-4 h-4" weight="bold" />}
              >
                Add another mailbox
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Sender row action menu — portalled per §19 so the card's clipping and
          stacking context can't swallow it. */}
      {openMenu != null && menuCoords && (() => {
        const sender = senderEmails.find(s => s.id === openMenu)
        if (!sender) return null
        const name = sender.label || sender.email_address
        return createPortal(
          <>
            <div className="fixed inset-0 z-[80]" onClick={() => closeMenu(false)} aria-hidden="true" />
            <div
              ref={menuRef}
              id={`sender-menu-${sender.id}`}
              role="menu"
              aria-label={`Actions for ${name}`}
              onKeyDown={onMenuKeyDown}
              style={{ position: 'fixed', top: menuCoords.top, right: menuCoords.right }}
              className={MENU}
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => { setEditingSender(sender); closeMenu() }}
                className={MENU_ITEM}
                style={inputStyle}
                disabled={!canWrite}
              >
                <PencilSimple className="w-3 h-3" /> Edit
              </button>
              {/* Once the connection is proven there is nothing to re-run, so
                  this reads as a state rather than an action.

                  Gated on BOTH flags, not on status: status flips to
                  'verified' when SMTP alone succeeds (services/sender_email.py
                  recomputes it that way), so keying off it would grey out the
                  one control that fixes a broken IMAP. That matters now that a
                  refused IMAP login demotes imap_verified on its own -- Verify
                  is the way back, and disabling it would strand the mailbox. */}
              {isFullyVerified(sender) ? (
                <div
                  className={`${MENU_ITEM} cursor-default text-emerald-700 dark:text-emerald-300`}
                  style={inputStyle}
                  role="status"
                >
                  <ShieldCheck className="w-3 h-3" weight="fill" /> Verified
                </div>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { handleVerify(sender.id); closeMenu() }}
                  disabled={verifyingId === sender.id}
                  className={MENU_ITEM}
                  style={inputStyle}
                >
                  {verifyingId === sender.id ? <CircleNotch className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                  Verify
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => { checkDns(sender.id); closeMenu() }}
                className={MENU_ITEM}
                style={inputStyle}
              >
                <Globe className="w-3 h-3" /> Check DNS
              </button>
              {!sender.is_default && (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => { setDefault(sender.id); closeMenu() }}
                  className={MENU_ITEM}
                  style={inputStyle}
                >
                  <Star className="w-3 h-3" weight="fill" /> Set Default
                </button>
              )}
              <hr className="my-1.5 border-border/50 dark:border-white/[0.06]" />
              <button
                type="button"
                role="menuitem"
                onClick={() => { setPendingDelete(sender); closeMenu() }}
                className="w-full text-left px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-500/[0.08] dark:hover:bg-red-500/[0.10] focus:outline-none focus-visible:bg-red-500/[0.10] transition-colors duration-150 flex items-center gap-2"
                style={inputStyle}
              >
                <Trash className="w-3 h-3" />
                Delete
              </button>
            </div>
          </>,
          document.body,
        )
      })()}

      {showAddModal && (
        <AddSenderEmailModal
          onClose={() => { setShowAddModal(false); fetchSenderEmails() }}
          onDirtyChange={setModalDirty}
        />
      )}
      {editingSender && (
        <EditSenderEmailModal
          sender={editingSender}
          onClose={() => { setEditingSender(null); fetchSenderEmails() }}
          onDirtyChange={setModalDirty}
        />
      )}
      {accessModalSender && (
        <ManageAccessModal
          senderLabel={accessModalSender.label}
          senderEmailAddress={accessModalSender.email_address}
          members={(accessModalSender.assigned_user_ids ?? [])
            .map(uid => members.find(m => m.id === uid))
            .filter((m): m is AssignableMember => Boolean(m))}
          roleLabel={(role) => MEMBER_ROLE_LABELS[role] ?? role}
          onRevoke={(userId) => assignSender(
            accessModalSender.id,
            (accessModalSender.assigned_user_ids ?? []).filter(id => id !== userId),
          )}
          onClose={() => setAccessModalSenderId(null)}
        />
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete mailbox"
        description={
          <>Are you sure you want to delete <span className="font-medium text-foreground dark:text-[#f7f8f8]">{pendingDelete?.label || pendingDelete?.email_address}</span>? This action cannot be undone.</>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="destructive"
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  )
}

export default EmailAccountsSection
